import type { Canvas, FabricObject } from 'fabric';
import {
  captureObjectState,
  getAnimationStateAt,
  getAnimationTimelineMs,
  stopAllAnimations,
  type AnimatableObject,
  type AnimationConfig,
  type ObjectState,
} from './CanvasAnimations';
import { findSkuImageUrl, readBatchRecords, type BatchRecord, type BatchValidationProgress } from './canvasBatch';
import { getLayerObjects, getLayerType, type LayerObject } from './CanvasLayers';
import type { FlushSave } from '@/hooks/canvas/useCanvasAutosave';
import { canvasService } from '@/services/canvasService';
import { downloadFile } from '@/utils/file';
import { toFullImageUrl, toSecureImageUrl } from '@/utils/imageUrl';
import { canEncodeIndexedPng, encodeIndexedPng } from '@/utils/pngEncoder';

export { buildBatchFormatCsv } from './canvasBatch';

export type ExportImageType = 'png' | 'jpg' | 'gif';
export type ExportQuality = 'low' | 'medium' | 'high';

// Every quality keeps the canvas dimensions; lower ones only trade compression or colors for weight.
// Alta is the untouched render. PNG Media/Baja become palette PNGs (fewer colors, same pixels).
export const JPG_QUALITY: Record<ExportQuality, number> = { high: 1, medium: 0.8, low: 0.6 };
export const PNG_COLORS: Record<Exclude<ExportQuality, 'high'>, number> = { medium: 256, low: 64 };
// GIF frame delays are stored in 1/100 s, hence 50/70/100 ms (20, ~14 and 10 fps).
export const GIF_SETTINGS: Record<ExportQuality, { colors: number; frameMs: number }> = {
  high: { colors: 256, frameMs: 50 },
  medium: { colors: 128, frameMs: 70 },
  low: { colors: 64, frameMs: 100 },
};
// Longest GIF exported: past this the file grows without much use.
export const MAX_GIF_DURATION_MS = 15000;

type GuideObject = FabricObject & { isGridLine?: boolean; isSafeZone?: boolean };

// The canvas without the guides or the selection controls. Multiplier 1 is its exact size (the editor
// zoom is CSS only).
const renderCanvas = (canvas: Canvas, multiplier = 1): HTMLCanvasElement =>
  canvas.toCanvasElement(multiplier, {
    filter: (object) => !(object as GuideObject).isGridLine && !(object as GuideObject).isSafeZone,
  });

const getPixels = (element: HTMLCanvasElement): ImageData => {
  const context = element.getContext('2d');
  if (!context) throw new Error('No se pudo generar la imagen');
  return context.getImageData(0, 0, element.width, element.height);
};

const toBlob = (element: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    element.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No se pudo generar la imagen'))), type, quality);
  });

// Lets the browser paint (progress, spinner) between heavy steps.
const nextTick = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

export const getAnimatedObjects = (canvas: Canvas): AnimatableObject[] =>
  (getLayerObjects(canvas) as AnimatableObject[]).filter((object) => object.animationConfig?.type);

// How long playing every animation takes (the longest one), capped like the GIF.
const getTimelineMs = (animated: AnimatableObject[]) =>
  Math.min(
    MAX_GIF_DURATION_MS,
    Math.max(...animated.map((object) => getAnimationTimelineMs(object.animationConfig as AnimationConfig)))
  );

// Puts every animated object in its state `elapsedMs` into the playback of all animations (entrances
// hidden until they start, exits gone once they end). Used by the GIF and the preview so both show exactly the same.
const applyAnimationFrame = (animated: AnimatableObject[], originals: ObjectState[], elapsedMs: number) => {
  animated.forEach((object, index) =>
    object.set(
      getAnimationStateAt(object.animationConfig as AnimationConfig, originals[index], elapsedMs, {
        hideBeforeEntrance: true,
        hideAfterExit: true,
      })
    )
  );
};

const restoreObjects = (canvas: Canvas, animated: AnimatableObject[], originals: ObjectState[]) => {
  animated.forEach((object, index) => object.set(originals[index]));
  canvas.requestRenderAll();
};

// Plays every animation once into `target` (a small preview <canvas>), frame by frame as the GIF would
// show them. The editor canvas isn't repainted meanwhile. Returns a function that stops it; either way
// the objects get back their state and `onEnd` runs.
export const playAnimationPreview = (
  canvas: Canvas,
  target: HTMLCanvasElement,
  multiplier: number,
  onEnd: () => void
): (() => void) => {
  stopAllAnimations(canvas);
  const animated = getAnimatedObjects(canvas);
  const originals = animated.map(captureObjectState);
  const durationMs = animated.length > 0 ? getTimelineMs(animated) : 0;
  const context = target.getContext('2d');
  let frameId: number | null = null;
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frameId !== null) cancelAnimationFrame(frameId);
    restoreObjects(canvas, animated, originals);
    onEnd();
  };

  const startTime = performance.now();
  const drawFrame = (now: number) => {
    if (stopped) return;
    const elapsedMs = Math.min(now - startTime, durationMs);
    try {
      applyAnimationFrame(animated, originals, elapsedMs);
      const frame = renderCanvas(canvas, multiplier);
      target.width = frame.width;
      target.height = frame.height;
      context?.drawImage(frame, 0, 0);
    } catch (error) {
      console.error('Error al reproducir la vista previa:', error);
      stop();
      return;
    }
    if (elapsedMs >= durationMs) stop();
    else frameId = requestAnimationFrame(drawFrame);
  };

  drawFrame(startTime);
  return stop;
};

type SwappableImage = LayerObject & {
  setSrc?: (src: string, options?: { crossOrigin?: string | null }) => Promise<void>;
  getSrc?: () => string;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  setCoords?: () => void;
};

type ImageFrame = {
  src: string;
  left: number | undefined;
  top: number | undefined;
  angle: number | undefined;
  originX: SwappableImage['originX'];
  originY: SwappableImage['originY'];
  skewX: number | undefined;
  skewY: number | undefined;
  flipX: boolean | undefined;
  flipY: boolean | undefined;
  scaleX: number;
  scaleY: number;
  width: number;
  height: number;
};

const captureImage = (image: SwappableImage): ImageFrame => ({
  src: image.getSrc?.() ?? '',
  left: image.left,
  top: image.top,
  angle: image.angle,
  originX: image.originX,
  originY: image.originY,
  skewX: image.skewX,
  skewY: image.skewY,
  flipX: image.flipX,
  flipY: image.flipY,
  scaleX: image.scaleX ?? 1,
  scaleY: image.scaleY ?? 1,
  width: image.width || 1,
  height: image.height || 1,
});

// New picture, same place and size the capa already has on the canvas.
const placeImage = async (image: SwappableImage, src: string, frame: ImageFrame) => {
  if (!image.setSrc || !src) return;
  await image.setSrc(src, { crossOrigin: 'anonymous' });
  const width = image.width || frame.width;
  const height = image.height || frame.height;
  image.set({
    left: frame.left,
    top: frame.top,
    angle: frame.angle,
    originX: frame.originX,
    originY: frame.originY,
    skewX: frame.skewX,
    skewY: frame.skewY,
    flipX: frame.flipX,
    flipY: frame.flipY,
    scaleX: (frame.width * frame.scaleX) / width,
    scaleY: (frame.height * frame.scaleY) / height,
  });
  image.setCoords?.();
};

const sameSku = (object: LayerObject, value: string) => {
  const wanted = value.trim().toLowerCase();
  const name = object.layerName?.trim().toLowerCase() ?? '';
  const id = object.sourceRef?.id === undefined ? '' : String(object.sourceRef.id).toLowerCase();
  return wanted === name || wanted === id;
};

const applyRecord = async (
  skuLayers: SwappableImage[],
  textLayers: LayerObject[],
  record: BatchRecord,
  swapped: { image: SwappableImage; frame: ImageFrame }[],
) => {
  for (const [index, value] of record.skus.entries()) {
    const image = skuLayers[index];
    if (!image || sameSku(image, value)) continue;
    const url = await findSkuImageUrl(value);
    if (!url) throw new Error(`No se encontró la imagen del SKU ${value}`);
    const frame = captureImage(image);
    swapped.push({ image, frame });
    await placeImage(image, toFullImageUrl(toSecureImageUrl(url)), frame);
  }
  record.texts.forEach((value, index) => {
    const text = textLayers[index] as LayerObject & { initDimensions?: () => void };
    if (!text) return;
    text.set({ text: value });
    text.initDimensions?.();
  });
};

const restoreRecord = async (swapped: { image: SwappableImage; frame: ImageFrame }[], textLayers: LayerObject[], texts: string[]) => {
  for (const { image, frame } of swapped) {
    if (frame.src) await placeImage(image, frame.src, frame);
    else image.set(frame);
  }
  textLayers.forEach((text, index) => text.set({ text: texts[index] }));
};

// One PNG per filled row, rendered the same way as Individual → PNG, with that row's SKUs and texts.
export const exportBatchRowPngs = async (
  canvas: Canvas,
  file: File,
  onProgress?: (progress: BatchValidationProgress) => void,
  renderPng: (canvas: Canvas) => Promise<Blob> = (current) => exportStaticImage(current, 'png', 'high'),
): Promise<Blob[]> => {
  const records = await readBatchRecords(file, canvas);
  const layers = getLayerObjects(canvas);
  const skuLayers = layers.filter((object) => getLayerType(object) !== 'text' && object.sourceRef?.kind === 'sku') as SwappableImage[];
  const textLayers = layers.filter((object) => getLayerType(object) === 'text');
  const originalTexts = textLayers.map((object) => String((object as LayerObject & { text?: string }).text ?? ''));
  const pngs: Blob[] = [];

  onProgress?.({ processed: 0, total: records.length });
  try {
    for (const [index, record] of records.entries()) {
      const swapped: { image: SwappableImage; frame: ImageFrame }[] = [];
      try {
        await applyRecord(skuLayers, textLayers, record, swapped);
        pngs.push(await renderPng(canvas));
      } finally {
        await restoreRecord(swapped, textLayers, originalTexts);
      }
      onProgress?.({ processed: index + 1, total: records.length });
      await nextTick();
    }
  } finally {
    canvas.requestRenderAll();
  }
  return pngs;
};

export const exportStaticImage = async (canvas: Canvas, type: 'png' | 'jpg', quality: ExportQuality): Promise<Blob> => {
  stopAllAnimations(canvas);
  const element = renderCanvas(canvas);

  if (type === 'jpg') return toBlob(element, 'image/jpeg', JPG_QUALITY[quality]);
  if (quality === 'high' || !canEncodeIndexedPng()) return toBlob(element, 'image/png');

  const { quantize, applyPalette } = await import('gifenc');
  const { data, width, height } = getPixels(element);
  const palette = quantize(data, PNG_COLORS[quality]);
  return encodeIndexedPng({ width, height, palette, indices: applyPalette(data, palette) });
};

// Renders the animations frame by frame, like the editor plays them all together, and encodes each frame
// as soon as it's rendered (only one frame in memory). Objects get back their state at the end.
export const exportGif = async (
  canvas: Canvas,
  quality: ExportQuality,
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  stopAllAnimations(canvas);
  const animated = getAnimatedObjects(canvas);
  if (animated.length === 0) throw new Error('El GIF requiere al menos un elemento con animación');

  const { GIFEncoder, quantize, applyPalette } = await import('gifenc');
  const { colors, frameMs } = GIF_SETTINGS[quality];
  const durationMs = getTimelineMs(animated);
  // Frames at 0, frameMs, ... up to the end, which shows every element back in its final place.
  const frameCount = Math.ceil(durationMs / frameMs) + 1;
  const originals = animated.map(captureObjectState);
  const gif = GIFEncoder();

  try {
    for (let frame = 0; frame < frameCount; frame++) {
      applyAnimationFrame(animated, originals, Math.min(frame * frameMs, durationMs));
      const { data, width, height } = getPixels(renderCanvas(canvas));
      const palette = quantize(data, colors);
      gif.writeFrame(applyPalette(data, palette), width, height, { palette, delay: frameMs, repeat: 0 });
      onProgress?.((frame + 1) / frameCount);
      await nextTick();
    }
    gif.finish();
    return new Blob([gif.bytes()], { type: 'image/gif' });
  } finally {
    restoreObjects(canvas, animated, originals);
  }
};

// The canvas entity as the backend has it (structure included). Pending changes are saved first so the
// file never misses the last edits.
const SAVE_FAILED_MESSAGE = 'No se pudieron guardar los últimos cambios. Inténtalo de nuevo.';

export const exportCanvasJson = async (canvasId: string, flushSave: FlushSave): Promise<Blob> => {
  if (!(await flushSave())) throw new Error(SAVE_FAILED_MESSAGE);
  const entity = await canvasService.getCanvas(canvasId);
  return new Blob([JSON.stringify(entity, null, 2)], { type: 'application/json' });
};

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] as string);

// Minimal page showing the canvas image the backend stores (linked, not embedded, so the file is a few
// hundred bytes). width/height reserve the image's space before it loads (no layout shift) and the
// inline style lets it shrink on narrow screens keeping its proportions.
export const buildCanvasHtml = ({ title, imageUrl, width, height }: { title: string; imageUrl: string; width: number; height: number }) => {
  const safeTitle = escapeHtml(title || 'Canvas');
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${safeTitle}</title>
</head>
<body style="margin:0">
<img src="${escapeHtml(imageUrl)}" width="${width}" height="${height}" alt="${safeTitle}" style="display:block;max-width:100%;height:auto">
</body>
</html>
`;
};

// HTML with an <img> linking the canvas image. The pending changes and an outdated image are saved first,
// so the link shows the canvas as it is now. The model's `image` is the thumbnail: the link drops /thumb
// to point to the full image.
export const exportCanvasHtml = async (canvasId: string, flushSave: FlushSave): Promise<Blob> => {
  if (!(await flushSave({ withImage: true }))) throw new Error(SAVE_FAILED_MESSAGE);
  const { data } = await canvasService.getCanvas(canvasId);
  const html = buildCanvasHtml({
    title: data.title,
    imageUrl: toSecureImageUrl(toFullImageUrl(data.image)),
    width: data.width,
    height: data.height,
  });
  return new Blob([html], { type: 'text/html' });
};

// "Promo Verano 2026!" -> "promo-verano-2026.png"
export const toExportFileName = (title: string, extension: string): string => {
  const slug = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z\d]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'canvas'}.${extension}`;
};

// Revoked a bit later: some browsers start the download asynchronously after the click.
const REVOKE_DELAY_MS = 1000;

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  downloadFile(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
};
